import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TableCell as MuiTableCell,
    Button
} from '@mui/material';
import { styled, tableCellClasses } from '@mui/material';
import { PrimaryButton } from '../components';
import InfoHeader from '../components/InfoHeader';
import BasicMenu from '../components/BasicMenu';
import SelectAcao from '../components/SelectAcao';
import SelectColaborador from '../components/SelectColaborador';
import { desdobramentoService } from '../services/DesdobramentoService';
import { Desdobramento } from '../types/negocio/Desdobramento';
import PlanoAcao from '../types/negocio/PlanoAcao';
import toastService from '../utils/toastService';
import TarefaModal from '../components/modais/TarefaModal';
import LoadingIndicator from '../components/LoadingIndicator';

const StyledTableCell = styled(MuiTableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#042E4B',
        color: theme.palette.common.white,
        fontWeight: 'bold'
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const Tarefas: React.FC = () => {
    const [desdobramentos, setDesdobramentos] = useState<Desdobramento[]>([]);
    const [selectedAcao, setSelectedAcao] = useState<PlanoAcao | undefined>();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [modalAberto, setModalAberto] = useState(false);
    const [modoEdicao, setModoEdicao] = useState(false);
    const [tarefaSelecionada, setTarefaSelecionada] = useState<Desdobramento>({} as Desdobramento);
    const [responsavel, setResponsavel] = useState("");

    const criarTarefaVazia = (): Desdobramento => ({
        guid: '',
        planoAcaoGuid: selectedAcao?.guid || "",
        oque: "",
        porQue: "",
        quem: "",
        quemNomeCompleto: "",
        como: "",
        quando: "",
        onde: "",
        quantoCusta: "",
        situacaoKanban: "Pendente",
    });

    useEffect(() => {
        if (selectedAcao?.guid) {
            fetchTarefas();
        }
    }, [selectedAcao]);

    const fetchTarefas = async () => {
        if (!selectedAcao?.guid) return;

        try {
            setIsLoading(true);
            setError(null);
            const data = await desdobramentoService.listarPorPlano(selectedAcao.guid, {
                responsavel: responsavel || undefined,
            });
            
            if (Array.isArray(data)) {
                setDesdobramentos(data);
            } else {
                console.error('Resposta inesperada ao carregar tarefas:', data);
                toastService.error('Formato de dados inesperado. Entre em contato com o suporte.');
                setDesdobramentos([]);
            }
        } catch (error) {
            console.error('Erro ao carregar tarefas:', error);
            setError("Não foi possível carregar os dados das tarefas. Por favor, tente novamente.");
            toastService.error('Erro ao carregar tarefas. Tente novamente mais tarde.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (index: number) => {
        setTarefaSelecionada(desdobramentos[index]);
        setModoEdicao(true);
        setModalAberto(true);
    };

    const handleNovaTarefa = () => {
        if (!selectedAcao?.guid) {
            toastService.error("Selecione uma ação primeiro.");
            return;
        }
        
        setTarefaSelecionada(criarTarefaVazia());
        setModoEdicao(true);
        setModalAberto(true);
    };

    const handleViewMore = (index: number) => {
        setTarefaSelecionada(desdobramentos[index]);
        setModoEdicao(false);
        setModalAberto(true);
    };

    const handleSaveTarefa = async (data: Desdobramento) => {
        try {
            const isNovaTarefa = !data.guid || data.guid.trim() === '';
            
            if (isNovaTarefa) {
                await desdobramentoService.criar(data);
                toastService.success('Tarefa criada com sucesso!');
            } else {
                await desdobramentoService.atualizar(data);
                toastService.success('Tarefa atualizada com sucesso!');
            }
            
            setModalAberto(false);
            
            // Atualizar a lista
            if (selectedAcao?.guid) {
                const listaAtualizada = await desdobramentoService.listarPorPlano(selectedAcao.guid, {
                    responsavel: responsavel || undefined,
                });
                setDesdobramentos(listaAtualizada);
            }
        } catch (error) {
            console.error('Erro ao salvar tarefa:', error);
            toastService.error('Erro ao salvar tarefa.');
        }
    };

    const handleDelete = async (index: number) => {
        const tarefaToDelete = desdobramentos[index];

        if (!tarefaToDelete?.guid) {
            toastService.error("Tarefa não encontrada.");
            return;
        }

        try {
            await desdobramentoService.deletar(tarefaToDelete.guid);
            const novaLista = desdobramentos.filter((_, i) => i !== index);
            setDesdobramentos(novaLista);
            toastService.success("Tarefa excluída com sucesso!");
        } catch (error) {
            console.error("Erro ao excluir tarefa:", error);
            toastService.error("Erro ao excluir tarefa. Tente novamente.");
        }
    };

    // Show loading state when data is being fetched
    if (isLoading && selectedAcao) {
        return <LoadingIndicator fullHeight message="Carregando tarefas..." />;
    }

    // Show error state only after loading is complete and there's an error
    if (error && !isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
                <Typography color="error">{error}</Typography>
                <PrimaryButton onClick={() => window.location.reload()}>
                    Tentar Novamente
                </PrimaryButton>
            </Box>
        );
    }

    return (
        <Box>
            <InfoHeader
                title="Tarefas"
                message="Adicione tarefas para as ações criadas no plano de ação em pequenas tarefas para facilitar o caminho até a conclusão da mesma."
                isImportant={true}
                url="https://youtu.be/MHPv-tSLTpU"
                modalTitle="Entenda a aba Tarefas"
            />

            {/* Filtros */}
            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "flex-end" }}>
                <SelectAcao
                    sx={{ flexGrow: 1 }}
                    value={selectedAcao ?? null}
                    onChange={(newValue) => setSelectedAcao(newValue)}
                />

                <Box sx={{ flexGrow: 1 }}>
                    <SelectColaborador
                        value={responsavel}
                        onChange={(colaborador) => setResponsavel(colaborador?.guid ?? '')}
                    />
                </Box>

                <PrimaryButton
                    variant="contained"
                    color="primary"
                    onClick={fetchTarefas}
                    sx={{ minWidth: "120px", height: "56px" }}
                >
                    Filtrar
                </PrimaryButton>
            </Box>

            <Paper elevation={3} sx={{ mt: 2 }}>
                <TableContainer sx={{ borderRadius: '8px' }}>
                    <Table aria-label="tarefas table">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell><strong>Tarefa</strong></StyledTableCell>
                                <StyledTableCell><strong>Importância</strong></StyledTableCell>
                                <StyledTableCell><strong>Responsável</strong></StyledTableCell>
                                <StyledTableCell><strong>Prazo</strong></StyledTableCell>
                                <StyledTableCell><strong>Valor</strong></StyledTableCell>
                                <StyledTableCell align="center"><strong>Ações</strong></StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!selectedAcao ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={6} align="center">
                                        <Typography sx={{ color: 'text.secondary', py: 2 }}>
                                            Selecione um plano de ação para visualizar as tarefas
                                        </Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : desdobramentos.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={6} align="center">
                                        <Typography sx={{ color: 'text.secondary', py: 2 }}>
                                            Nenhuma tarefa cadastrada
                                        </Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : (
                                desdobramentos.map((tarefa, index) => (
                                    <StyledTableRow key={index}>
                                        <StyledTableCell 
                                            component="th" 
                                            scope="row"
                                            onClick={() => handleViewMore(index)}
                                            sx={{ cursor: "pointer" }}
                                        >
                                            <Typography variant="body2" sx={{ fontSize: 16, "&:hover": { textDecoration: "underline", color: "primary.main" } }}>
                                                {tarefa.oque || '-'}
                                            </Typography>
                                        </StyledTableCell>
                                        <StyledTableCell component="th" scope="row">
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: 16,
                                                    color: tarefa.porQue && tarefa.porQue.trim() !== ''
                                                        ? 'text.primary'
                                                        : 'secondary.main'
                                                }}
                                            >
                                                {tarefa.porQue || 'Sem descrição'}
                                            </Typography>
                                        </StyledTableCell>
                                        <StyledTableCell component="th" scope="row">
                                            <Typography variant="body2" sx={{ fontSize: 16 }}>
                                                {tarefa.quemNomeCompleto || '-'}
                                            </Typography>
                                        </StyledTableCell>
                                        <StyledTableCell component="th" scope="row">
                                            <Typography variant="body2" sx={{ fontSize: 16 }}>
                                                {tarefa.quando || '-'}
                                            </Typography>
                                        </StyledTableCell>
                                        <StyledTableCell component="th" scope="row">
                                            <Typography variant="body2" sx={{ fontSize: 16 }}>
                                                {tarefa.quantoCusta ? `R$ ${parseFloat(tarefa.quantoCusta).toLocaleString('pt-BR', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}` : '-'}
                                            </Typography>
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <BasicMenu
                                                onEdit={() => handleEdit(index)}
                                                onDelete={() => handleDelete(index)}
                                                onViewMore={() => handleViewMore(index)}
                                            />
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                <PrimaryButton 
                    variant="contained" 
                    onClick={handleNovaTarefa}
                    disabled={!selectedAcao}
                >
                    Adicionar
                </PrimaryButton>
            </Box>

            <TarefaModal
                open={modalAberto}
                onClose={() => setModalAberto(false)}
                desdobramento={tarefaSelecionada}
                modoEdicao={modoEdicao}
                setDesdobramento={modoEdicao ? setTarefaSelecionada : undefined}
                onSave={modoEdicao ? handleSaveTarefa : undefined}
                onEditClick={() => setModoEdicao(true)}
                isNovaTarefa={!tarefaSelecionada?.guid || tarefaSelecionada.guid.trim() === ''}
            />
        </Box>
    );
};

export default Tarefas;